import { allPieceTypes, animalPieceTypes } from "../const";
import {
  IPiece,
  PieceMovement,
  Position,
  PieceType,
  IMove,
  MoveType,
  ISquare,
  Effect,
} from "../types";
import { BaseMovement } from "./BaseMovement";
import { Move } from "../Move";
import { SideEffect } from "../SideEffect";

const orthogonalDirections = [
  [-1, 0], // Up
  [0, 1], // Right
  [1, 0], // Down
  [0, -1], // Left
];

const diagonalDirections = [
  [-1, -1], // Top Left
  [-1, 1], // Top Right
  [1, 1], // Bottom Right
  [1, -1], // Bottom Left
];

export class OctopusMovement extends BaseMovement implements PieceMovement {
  capturablePieceTypes: PieceType[] = allPieceTypes;
  entanglablePieceTypes: PieceType[] = animalPieceTypes;

  getValidMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    isLightTurn?: boolean
  ): IMove[] {
    // ZOMBIE INK: Allow shadows to generate moves so owner can see movement options
    // Normal shadows (from regular ink) still return empty - opponent shouldn't see moves
    if (piece.isShadow) {
      // Zombie ink shadows: owner needs to see moves to play the guessing game
      if (piece.isZombieInkShadow && piece.zombieInkTargetColor === isLightTurn) {
        // Continue to generate moves - don't return early
      } else {
        return []; // Normal shadow or wrong player - no moves
      }
    }

    // ZOMBIFICATION: Zombified pieces act as the opposite color
    const effectiveIsLight = piece.zombifiedStatus.isZombified
      ? !piece.isLight
      : piece.isLight;

    const regularMoves = this.getValidRegularMoves(piece, position, boardState, effectiveIsLight);
    const entangleMoves = this.getValidEntangleMoves(
      piece,
      position,
      boardState,
      effectiveIsLight
    );
    const inkMoves = this.getValidInkMoves(piece, position, boardState, effectiveIsLight);

    return [...regularMoves, ...entangleMoves, ...inkMoves];
  }

  private getValidRegularMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    // Check all surrounding squares
    for (const direction of diagonalDirections) {
      const toPosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      if (!this.onBoard(boardState, toPosition)) continue;

      const toSquare = boardState[toPosition.row][toPosition.col];
      const squareInfo = this.getSquareInfo(toSquare, effectiveIsLight);

      // Special case for fainted goats - treat them as if they aren't there
      const hasFaintedGoat =
        toSquare.Pieces.length === 1 &&
        toSquare.Pieces[0].type === PieceType.Goat &&
        toSquare.Pieces[0].isFainted;

      if (squareInfo.basicSquareInfo?.isEmpty || hasFaintedGoat) {
        // If it's a fainted goat, we can move there as if it's an empty square
        possibleMoves.push(new Move(piece, toPosition, MoveType.regular, null));
        continue;
      }

      // Check if the target square has a tree (can be alone or with another piece)
      const hasTree = toSquare.Pieces.some((p) => p.type === PieceType.Tree);

      if (hasTree) {
        const tree = toSquare.Pieces.find((p) => p.type === PieceType.Tree);
        const housedPiece = toSquare.Pieces.find((p) => p.type !== PieceType.Tree);

        if (housedPiece) {
          // Housing never grants a diagonal capture: Octopus captures only
          // orthogonally. An occupied Tree blocks diagonal entry for either side.
          continue;
        } else if (tree) {
          // Tree is empty or tree is an enemy tree - octopus can climb it diagonally
          // ZOMBIFICATION FIX: Use effectiveIsLight for ally detection
          if (tree.isLight === effectiveIsLight) {
            // Friendly tree - can climb
            possibleMoves.push(new Move(piece, toPosition, MoveType.regular, null));
            continue;
          } else {
            // Enemy tree - can also climb (octopus cannot capture or entangle trees diagonally)
            possibleMoves.push(new Move(piece, toPosition, MoveType.regular, null));
            continue;
          }
        }
      }
    }

    // Check orthogonal squares for capturable enemy pieces
    for (const direction of orthogonalDirections) {
      const toPosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      if (!this.onBoard(boardState, toPosition)) continue;

      const toSquare = boardState[toPosition.row][toPosition.col];

      // Special case for fainted goats - treat them as if they aren't there
      const hasFaintedGoat =
        toSquare.Pieces.length === 1 &&
        toSquare.Pieces[0].type === PieceType.Goat &&
        toSquare.Pieces[0].isFainted;

      if (hasFaintedGoat) {
        // If it's a fainted goat, we can move there as if it's an empty square
        possibleMoves.push(new Move(piece, toPosition, MoveType.regular, null));
        continue;
      }

      // Check if the target square has multiple pieces (potentially a tree housing another piece)
      if (toSquare.Pieces.length > 1) {
        // Check if one of the pieces is a tree
        const hasTree = toSquare.Pieces.some((p) => p.type === PieceType.Tree);

        if (hasTree) {
          // Find the non-tree piece in the square
          const housedPiece = toSquare.Pieces.find(
            (p) => p.type !== PieceType.Tree
          );

          if (housedPiece) {
            // ZOMBIFICATION FIX: Use effectiveIsLight for ally detection
            if (housedPiece.isLight === effectiveIsLight) {
              // If the housed piece is friendly, skip this move
              continue;
            } else {
              // If the housed piece is an enemy, create a capture move
              possibleMoves.push(
                new Move(piece, toPosition, MoveType.capture, housedPiece)
              );
              continue;
            }
          }
        }
      }

      const squareInfo = this.getSquareInfo(toSquare, effectiveIsLight);

      if (squareInfo.basicSquareInfo) {
        // Skip if this is a fainted goat (we already handled this case above)
        if (
          squareInfo.basicSquareInfo.piece?.type === PieceType.Goat &&
          squareInfo.basicSquareInfo.piece?.isFainted
        ) {
          continue;
        }

        // Use getMove instead of directly creating moves to respect piece-specific rules
        const move = this.getMove(
          position,
          toPosition,
          squareInfo.basicSquareInfo,
          piece,
          this.capturablePieceTypes
        );

        if (move) {
          possibleMoves.push(move);
        }
      }
    }

    return possibleMoves;
  }

  private getValidEntangleMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean
  ): IMove[] {
    const possibleMoves: IMove[] = [];

    const diagonalPositions = diagonalDirections
      .map((direction) => ({
        col: position.col + direction[0],
        row: position.row + direction[1],
      }))
      .filter((newPosition) => this.onBoard(boardState, newPosition));

    // Direct entanglement (from current position)
    // Create a separate move for each potential entangle target
    for (const diagonalPosition of diagonalPositions) {
      const squareInfo = this.getSquareInfo(
        boardState[diagonalPosition.row][diagonalPosition.col],
        effectiveIsLight
      );

      if (
        squareInfo.basicSquareInfo &&
        squareInfo.basicSquareInfo.piece &&
        squareInfo.basicSquareInfo.isEnemyOccupied &&
        this.entanglablePieceTypes.includes(
          squareInfo.basicSquareInfo.piece.type
        ) &&
        // Skip fainted goats - they cannot be entangled
        !(
          squareInfo.basicSquareInfo.piece.type === PieceType.Goat &&
          squareInfo.basicSquareInfo.piece.isFainted
        )
      ) {
        // Create a separate no-move for each entangle target
        var move = new Move(piece, position, MoveType.noMove, null);
        move.addSideEffect(
          diagonalPosition,
          [squareInfo.basicSquareInfo.piece],
          Effect.Entangle()
        );
        possibleMoves.push(move);
      }
    }

    // Triple entanglement - where the octopus can move to either side of an orthogonally adjacent enemy
    // Check each orthogonal direction for entanglable enemies
    for (const direction of orthogonalDirections) {
      const orthogonalPosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      if (!this.onBoard(boardState, orthogonalPosition)) continue;

      const orthogonalSquare =
        boardState[orthogonalPosition.row][orthogonalPosition.col];
      const orthogonalSquareInfo = this.getSquareInfo(
        orthogonalSquare,
        effectiveIsLight
      );

      // Check if there's an entanglable enemy piece in the orthogonal position
      if (
        orthogonalSquareInfo.basicSquareInfo &&
        orthogonalSquareInfo.basicSquareInfo.piece &&
        orthogonalSquareInfo.basicSquareInfo.isEnemyOccupied &&
        this.entanglablePieceTypes.includes(
          orthogonalSquareInfo.basicSquareInfo.piece.type
        ) &&
        // Skip fainted goats - they cannot be entangled
        !(
          orthogonalSquareInfo.basicSquareInfo.piece.type === PieceType.Goat &&
          orthogonalSquareInfo.basicSquareInfo.piece.isFainted
        )
      ) {
        // Determine the perpendicular direction (to find the side positions)
        let perpendicularDirections: number[][] = [];

        // If we're looking at a vertical direction (up/down), check the sides (left/right)
        if (direction[0] === -1 || direction[0] === 1) {
          perpendicularDirections = [
            [0, -1],
            [0, 1],
          ]; // Left and Right
        }
        // If we're looking at a horizontal direction (left/right), check up/down
        else if (direction[1] === -1 || direction[1] === 1) {
          perpendicularDirections = [
            [-1, 0],
            [1, 0],
          ]; // Up and Down
        }

        // Check each perpendicular position
        for (const perpDirection of perpendicularDirections) {
          const moveToPosition = {
            col: position.col + perpDirection[0],
            row: position.row + perpDirection[1],
          };

          // Skip if not on board or position is occupied
          if (!this.onBoard(boardState, moveToPosition)) continue;
          const moveToSquare =
            boardState[moveToPosition.row][moveToPosition.col];
          if (moveToSquare.Pieces.length > 0) continue;

          // Create the move
          var tripleMove = new Move(
            piece,
            moveToPosition,
            MoveType.regular,
            null
          );

          // Determine a name for the direction to make it easier to understand in the UI
          // In Zodiac coords: row 0 = bottom, row 7 = top. So row-1 = visually down, row+1 = visually up
          let directionName: string;
          if (perpDirection[0] === -1) directionName = "down";  // row-1 = visually down
          else if (perpDirection[0] === 1) directionName = "up"; // row+1 = visually up
          else if (perpDirection[1] === -1) directionName = "left";
          else directionName = "right";

          // Create a side effect for the entangle
          const tripleSideEffect = new SideEffect(
            tripleMove.id,
            orthogonalPosition, // Target the enemy piece in orthogonal position
            [orthogonalSquareInfo.basicSquareInfo.piece],
            Effect.Entangle()
          );

          // Store the movement position and direction information
          (tripleSideEffect as any).moveToPosition = moveToPosition;
          (tripleSideEffect as any).direction = directionName;
          // Mark this as a triple entangle for the UI
          (tripleSideEffect as any).tripleEntangle = true;
          // Store the orthogonal direction for reference (where the enemy is relative to octopus)
          // direction[0] === -1 means enemy is at row-1 = visually below = "down"
          (tripleSideEffect as any).orthogonalDirection =
            direction[0] !== 0
              ? direction[0] === -1
                ? "down"
                : "up"
              : direction[1] === -1
              ? "left"
              : "right";

          // Add the side effect to the move
          tripleMove.sideEffects.push(tripleSideEffect);
          possibleMoves.push(tripleMove);
        }
      }
    }

    // Indirect entanglement (knight's moves and 2-step diagonals)

    // Define knight's moves
    const knightMoves = [
      [2, 1],
      [1, 2],
      [-1, 2],
      [-2, 1],
      [-2, -1],
      [-1, -2],
      [1, -2],
      [2, -1],
    ];

    // Define two-step diagonal moves
    const twoStepDiagonalDirections = [
      [2, 2],
      [-2, 2],
      [2, -2],
      [-2, -2],
    ];

    // Combine knight's moves and two-step diagonals
    const allEntangleDirections = [
      ...knightMoves,
      ...twoStepDiagonalDirections,
    ];

    for (const direction of allEntangleDirections) {
      const entanglePosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      if (!this.onBoard(boardState, entanglePosition)) continue;

      // Determine toPosition based on the type of move
      let toPosition: Position | null = null;

      if (Math.abs(direction[0]) === 2 && Math.abs(direction[1]) === 2) {
        // Two-step diagonal moves
        toPosition = {
          col: position.col + direction[0] / 2,
          row: position.row + direction[1] / 2,
        };
      } else if (Math.abs(direction[0]) === 2 && Math.abs(direction[1]) === 1) {
        // Left or right knight moves
        if (direction[0] === -2) {
          // Left knight moves
          toPosition = {
            col: position.col - 1,
            row: position.row,
          };
        } else if (direction[0] === 2) {
          // Right knight moves
          toPosition = {
            col: position.col + 1,
            row: position.row,
          };
        }
      } else if (Math.abs(direction[0]) === 1 && Math.abs(direction[1]) === 2) {
        // Up or down knight moves
        if (direction[1] === -2) {
          // Up knight moves
          toPosition = {
            col: position.col,
            row: position.row - 1,
          };
        } else if (direction[1] === 2) {
          // Down knight moves
          toPosition = {
            col: position.col,
            row: position.row + 1,
          };
        }
      }

      // If toPosition couldn't be determined, skip this move
      if (!toPosition || !this.onBoard(boardState, toPosition)) continue;

      const toSquare = boardState[toPosition.row][toPosition.col];

      // If toSquare is occupied, skip this move
      if (toSquare.Pieces.length > 0) continue;

      const entangleSquare =
        boardState[entanglePosition.row][entanglePosition.col];
      const entangleSquareInfo = this.getSquareInfo(
        entangleSquare,
        effectiveIsLight
      );

      if (
        entangleSquareInfo.basicSquareInfo &&
        entangleSquareInfo.basicSquareInfo.piece &&
        entangleSquareInfo.basicSquareInfo.isEnemyOccupied &&
        this.entanglablePieceTypes.includes(
          entangleSquareInfo.basicSquareInfo.piece.type
        ) &&
        // Skip fainted goats - they cannot be entangled
        !(
          entangleSquareInfo.basicSquareInfo.piece.type === PieceType.Goat &&
          entangleSquareInfo.basicSquareInfo.piece.isFainted
        )
      ) {
        var move = new Move(piece, toPosition, MoveType.regular, null);

        // Add entangle side effect
        move.addSideEffect(
          entanglePosition,
          [entangleSquareInfo.basicSquareInfo.piece],
          Effect.Entangle()
        );

        possibleMoves.push(move);
      }
    }

    // Split entanglement (2 steps in cardinal directions)
    const splitMoveDirections = [
      [0, 2], // down
      [0, -2], // up
      [2, 0], // right
      [-2, 0], // left
    ];

    for (const direction of splitMoveDirections) {
      const entanglePosition = {
        col: position.col + direction[0],
        row: position.row + direction[1],
      };

      if (!this.onBoard(boardState, entanglePosition)) continue;

      const entangleSquare =
        boardState[entanglePosition.row][entanglePosition.col];
      const entangleSquareInfo = this.getSquareInfo(
        entangleSquare,
        effectiveIsLight
      );

      if (
        entangleSquareInfo.basicSquareInfo &&
        entangleSquareInfo.basicSquareInfo.piece &&
        entangleSquareInfo.basicSquareInfo.isEnemyOccupied &&
        this.entanglablePieceTypes.includes(
          entangleSquareInfo.basicSquareInfo.piece.type
        ) &&
        // Skip fainted goats - they cannot be entangled
        !(
          entangleSquareInfo.basicSquareInfo.piece.type === PieceType.Goat &&
          entangleSquareInfo.basicSquareInfo.piece.isFainted
        )
      ) {
        // Compute midPosition
        const midPosition = {
          col: position.col + direction[0] / 2,
          row: position.row + direction[1] / 2,
        };

        if (!this.onBoard(boardState, midPosition)) continue;

        // Determine perpendicular movement options
        if (direction[0] === 0) {
          // Vertical movement, diagonal options are to the left and right of midPosition
          const leftDiagonalPosition = {
            col: midPosition.col - 1,
            row: midPosition.row,
          };
          const rightDiagonalPosition = {
            col: midPosition.col + 1,
            row: midPosition.row,
          };

          // Create separate moves for each viable diagonal position
          if (this.onBoard(boardState, leftDiagonalPosition)) {
            // Check if the diagonal position is empty (can move there)
            const diagonalSquare =
              boardState[leftDiagonalPosition.row][leftDiagonalPosition.col];
            if (diagonalSquare.Pieces.length === 0) {
              // Create a move for left diagonal
              var leftMove = new Move(
                piece,
                leftDiagonalPosition,
                MoveType.regular,
                null
              );

              // Create a side effect on the target with direction information
              const leftSideEffect = new SideEffect(
                leftMove.id,
                entanglePosition, // Place the button on the target position
                [entangleSquareInfo.basicSquareInfo.piece],
                Effect.Entangle()
              );

              // Store the movement position and direction information
              (leftSideEffect as any).moveToPosition = leftDiagonalPosition;
              (leftSideEffect as any).direction = "left";

              // Add orthogonal direction information - direction[1] < 0 means moving toward lower col
              // The enemy is along the horizontal path; orthogonalDirection tells where octopus lands vertically
              (leftSideEffect as any).orthogonalDirection =
                direction[1] < 0 ? "up" : "down";

              // Add the side effect to the move
              leftMove.sideEffects.push(leftSideEffect);

              possibleMoves.push(leftMove);
            }
          }

          if (this.onBoard(boardState, rightDiagonalPosition)) {
            // Check if the diagonal position is empty (can move there)
            const diagonalSquare =
              boardState[rightDiagonalPosition.row][rightDiagonalPosition.col];
            if (diagonalSquare.Pieces.length === 0) {
              // Create a move for right diagonal
              var rightMove = new Move(
                piece,
                rightDiagonalPosition,
                MoveType.regular,
                null
              );

              // Create a side effect on the target with direction information
              const rightSideEffect = new SideEffect(
                rightMove.id,
                entanglePosition, // Place the button on the target position
                [entangleSquareInfo.basicSquareInfo.piece],
                Effect.Entangle()
              );

              // Store the movement position and direction information
              (rightSideEffect as any).moveToPosition = rightDiagonalPosition;
              (rightSideEffect as any).direction = "right";

              // Add orthogonal direction information - direction[1] < 0 means moving toward lower col
              (rightSideEffect as any).orthogonalDirection =
                direction[1] < 0 ? "up" : "down";

              // Add the side effect to the move
              rightMove.sideEffects.push(rightSideEffect);

              possibleMoves.push(rightMove);
            }
          }
        } else if (direction[1] === 0) {
          // Horizontal movement, diagonal options are above and below midPosition
          const upDiagonalPosition = {
            col: midPosition.col,
            row: midPosition.row - 1,
          };
          const downDiagonalPosition = {
            col: midPosition.col,
            row: midPosition.row + 1,
          };

          // Create separate moves for each viable diagonal position
          if (this.onBoard(boardState, upDiagonalPosition)) {
            // Check if the diagonal position is empty (can move there)
            const diagonalSquare =
              boardState[upDiagonalPosition.row][upDiagonalPosition.col];
            if (diagonalSquare.Pieces.length === 0) {
              // Create a move for up diagonal
              var upMove = new Move(
                piece,
                upDiagonalPosition,
                MoveType.regular,
                null
              );

              // Create a side effect on the target with direction information
              const upSideEffect = new SideEffect(
                upMove.id,
                entanglePosition, // Place the button on the target position
                [entangleSquareInfo.basicSquareInfo.piece],
                Effect.Entangle()
              );

              // Store the movement position and direction information
              // Note: upDiagonalPosition is row-1 which is visually DOWN (row 0 = bottom of board)
              (upSideEffect as any).moveToPosition = upDiagonalPosition;
              (upSideEffect as any).direction = "down";  // row-1 = visually down

              // Add orthogonal direction information - in this case, the octopus is left or right
              (upSideEffect as any).orthogonalDirection =
                direction[0] < 0 ? "right" : "left";

              // Add the side effect to the move
              upMove.sideEffects.push(upSideEffect);

              possibleMoves.push(upMove);
            }
          }

          if (this.onBoard(boardState, downDiagonalPosition)) {
            // Check if the diagonal position is empty (can move there)
            const diagonalSquare =
              boardState[downDiagonalPosition.row][downDiagonalPosition.col];
            if (diagonalSquare.Pieces.length === 0) {
              // Create a move for down diagonal
              var downMove = new Move(
                piece,
                downDiagonalPosition,
                MoveType.regular,
                null
              );

              // Create a side effect on the target with direction information
              const downSideEffect = new SideEffect(
                downMove.id,
                entanglePosition, // Place the button on the target position
                [entangleSquareInfo.basicSquareInfo.piece],
                Effect.Entangle()
              );

              // Store the movement position and direction information
              // Note: downDiagonalPosition is row+1 which is visually UP (row 7 = top of board)
              (downSideEffect as any).moveToPosition = downDiagonalPosition;
              (downSideEffect as any).direction = "up";  // row+1 = visually up

              // Add orthogonal direction information - in this case, the octopus is left or right
              (downSideEffect as any).orthogonalDirection =
                direction[0] < 0 ? "right" : "left";

              // Add the side effect to the move
              downMove.sideEffects.push(downSideEffect);

              possibleMoves.push(downMove);
            }
          }
        }
      }
    }

    return possibleMoves;
  }

  getValidInkMoves(
    piece: IPiece,
    position: Position,
    boardState: ISquare[][],
    effectiveIsLight: boolean
  ): IMove[] {
    const orthogonalPositions = orthogonalDirections
      .map((direction) => ({
        col: position.col + direction[0],
        row: position.row + direction[1],
      }))
      .filter((pos) => this.onBoard(boardState, pos));

    // Find empty orthogonal positions where we can ink
    const emptyPositions = orthogonalPositions.filter((pos) => {
      const square = boardState[pos.row][pos.col];
      const squareInfo = this.getSquareInfo(square, effectiveIsLight);
      return squareInfo.basicSquareInfo?.isEmpty;
    });

    return emptyPositions.map((inkTarget) => {
      // the move is on the ink target
      const inkMove = new Move(piece, inkTarget, MoveType.ink, null);

      // todo should we add ink side effect to the octopus itself here?
      // inkMove.addSideEffect(
      //   inkTarget,
      //   [], // No pieces affected
      //   Effect.Shadow()
      // );

      // Add shadow side effects to all other empty orthogonal squares
      emptyPositions
        .filter((pos) => pos.row !== inkTarget.row || pos.col !== inkTarget.col)
        .forEach((shadowPos) => {
          inkMove.addSideEffect(
            shadowPos,
            [], // No pieces affected
            Effect.Shadow()
          );
        });

      return inkMove;
    });
  }
}
